import React from "react";
import { Link } from "react-router-dom";
import { AnimatePresence } from "framer-motion";
import { Check, ArrowRight, PencilRuler, Landmark, Smartphone, Apple, Wallet, CreditCard } from "lucide-react";
import { Reveal } from "../components/Reveal";
import PricingCards from "../components/PricingCards";

export default function Services() {
  return (
    <div data-testid="services-page">
      {/* Hero */}
      <section className="bg-charcoal pt-40 pb-24">
        <div className="max-w-7xl mx-auto px-6 md:px-12 text-center">
          <Reveal>
            <span className="font-display text-sm uppercase tracking-widest font-semibold text-sky-brand">Pricing</span>
            <h1 className="font-display text-4xl md:text-6xl font-bold tracking-tight text-white mt-5">Choose your plan</h1>
            <p className="font-body text-lg text-white/60 mt-6 max-w-2xl mx-auto">Two simple ways to get your business online — pick the one that fits.</p>
          </Reveal>
        </div>
      </section>

      {/* Two-card pricing choice */}
      <section className="bg-white py-20 md:py-24 -mt-16">
        <div className="max-w-5xl mx-auto px-6 md:px-12">
          <PricingCards />
        </div>
      </section>

      {/* Site Edits — separate section */}
      <section className="bg-gray-50 py-20 md:py-24">
        <div className="max-w-5xl mx-auto px-6 md:px-12">
          <Reveal>
            <div className="text-center mb-12">
              <span className="font-display text-sm uppercase tracking-widest font-semibold text-sky-brand">Already have a site?</span>
              <h2 className="font-display text-3xl md:text-4xl font-semibold tracking-tight text-charcoal mt-4">Quick one-off edits</h2>
            </div>
          </Reveal>
          <Reveal delay={0.1}>
            <div data-testid="site-edits-card" className="bg-white border border-gray-100 rounded-[2rem] p-8 md:p-10 shadow-[0_8px_30px_rgb(0,0,0,0.04)] flex flex-col md:flex-row md:items-center gap-8">
              <div className="flex-1">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 rounded-2xl bg-sky-brand/15 flex items-center justify-center"><PencilRuler className="text-sky-brand" size={24} /></div>
                  <div>
                    <h3 className="font-display text-2xl font-semibold text-charcoal">Site Edits</h3>
                    <div className="flex items-baseline gap-1"><span className="font-display text-3xl font-bold text-sky-brand">$50</span><span className="font-body text-sm text-gray-400">/request</span></div>
                  </div>
                </div>
                <p data-testid="site-edits-fee-note" className="font-body text-xs text-gray-400 mt-2">A card processing fee applies at checkout.</p>
                <p className="font-body text-gray-500 mt-5 leading-relaxed">Any changes, updates or additions to your existing site — handled quickly and professionally, one request at a time. <span className="text-charcoal font-medium">On an active maintenance plan? Your first 3 edits each month are included - additional edits are just $25.</span></p>
                <ul className="mt-5 grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-2">
                  {["Text & wording updates", "Image & photo swaps", "New sections or pages", "Layout & styling tweaks", "Fast turnaround"].map((f) => (
                    <li key={f} className="flex items-start gap-2 font-body text-sm text-charcoal/80"><Check size={16} className="text-sky-brand shrink-0 mt-0.5" /> {f}</li>
                  ))}
                </ul>
              </div>
              <Link
                to="/edits"
                data-testid="site-edits-cta"
                className="font-display shrink-0 bg-charcoal text-white hover:bg-sky-brand hover:text-charcoal transition-colors duration-300 font-semibold px-8 py-4 rounded-full inline-flex items-center justify-center gap-2"
              >
                Request an Edit <ArrowRight size={16} />
              </Link>
            </div>
          </Reveal>
        </div>
      </section>

      {/* How to Pay */}
      <section className="bg-white py-20 md:py-24">
        <div className="max-w-5xl mx-auto px-6 md:px-12">
          <Reveal>
            <div className="text-center" data-testid="how-to-pay">
              <span className="font-display text-sm uppercase tracking-widest font-semibold text-sky-brand">How to Pay</span>
              <div className="mt-10 flex flex-wrap items-center justify-center gap-6 sm:gap-10">
                {[{ icon: Landmark, label: "Zelle" }, { icon: Smartphone, label: "Venmo" }, { icon: Apple, label: "Apple Pay" }, { icon: Wallet, label: "PayPal" }, { icon: CreditCard, label: "Stripe" }].map((m) => (
                  <div key={m.label} className="flex flex-col items-center gap-3 group">
                    <div className="w-16 h-16 rounded-2xl border border-gray-200 flex items-center justify-center transition-all duration-300 group-hover:border-sky-brand group-hover:bg-sky-brand/5">
                      <m.icon size={26} className="text-charcoal transition-colors duration-300 group-hover:text-sky-brand" />
                    </div>
                    <span className="font-display text-xs font-medium text-gray-500">{m.label}</span>
                  </div>
                ))}
              </div>
              <p className="font-body text-gray-500 mt-10 text-base md:text-lg">We make it easy — pay however works best for you.</p>
            </div>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
